import { Dialog } from '../components/Dialog';
import type { GameStore } from '../../store/GameStore';
import { formatCurrency } from '../../i18n/i18n';

function statBar(label: string, value: number, max: number, color: string): HTMLDivElement {
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;align-items:center;gap:6px;';

  const lbl = document.createElement('span');
  lbl.textContent = label;
  lbl.style.cssText = `font-size:11px;width:48px;color:${color};text-transform:capitalize;`;
  row.appendChild(lbl);

  const bar = document.createElement('div');
  bar.className = 'progress-bar';
  bar.style.cssText = 'flex:1;height:8px;';
  const fill = document.createElement('div');
  fill.className = 'progress-bar__fill';
  fill.style.cssText = `width:${Math.min(100, (value / max) * 100)}%;background:${color};`;
  bar.appendChild(fill);
  row.appendChild(bar);

  const val = document.createElement('span');
  val.textContent = String(Math.round(value));
  val.style.cssText = `font-size:11px;width:28px;text-align:right;font-family:var(--font-pixel);color:${color};`;
  row.appendChild(val);

  return row;
}

/**
 * Staff detail dialog — full info + actions for a single staff member.
 */
export function openStaffDetailDialog(
  parent: HTMLElement,
  store: GameStore,
  staffId: string,
  onClose: () => void,
): void {
  const state = store.getState();
  const theme = store.getThemeData();
  const staff = state.staff.find((s) => s.id === staffId);
  if (!staff) return;

  const prevSpeed = state.speed;
  store.dispatch({ type: 'SET_SPEED', speed: 0 });

  const errorEl = document.createElement('div');
  errorEl.style.cssText = 'color:var(--color-danger);font-size:11px;min-height:14px;margin-top:4px;';

  const dialog = new Dialog({
    title: staff.name,
    onClose: () => {
      store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
      onClose();
    },
    actions: [],
  });

  const content = document.createElement('div');
  content.style.cssText = 'display:flex;flex-direction:column;gap:10px;min-width:300px;';

  // Role + Level header
  const headerRow = document.createElement('div');
  headerRow.style.cssText = 'display:flex;justify-content:space-between;align-items:center;';

  const roleDef = theme.entities.staffRoles.find((r) => r.id === staff.role) ?? theme.entities.founderRole;
  const roleEl = document.createElement('span');
  roleEl.textContent = roleDef.name + (staff.isCEO ? ' (CEO)' : '');
  roleEl.style.cssText = 'font-size:12px;color:var(--color-text-secondary);';

  const levelEl = document.createElement('span');
  levelEl.style.cssText = 'font-size:14px;font-weight:700;color:var(--color-xp);font-family:var(--font-pixel);';
  levelEl.textContent = `Lv.${staff.level}`;

  headerRow.appendChild(roleEl);
  headerRow.appendChild(levelEl);
  content.appendChild(headerRow);

  // Salary + Team
  const infoRow = document.createElement('div');
  infoRow.style.cssText = 'display:flex;justify-content:space-between;font-size:11px;color:var(--color-text-muted);';

  const salaryEl = document.createElement('span');
  salaryEl.textContent = staff.isCEO ? 'No salary (CEO)' : `Salary: ${formatCurrency(staff.salary)}/wk`;

  const team = state.teams.find((t) => t.id === staff.teamId);
  const teamEl = document.createElement('span');
  teamEl.textContent = team ? `Team: ${team.name}` : 'No team';

  infoRow.appendChild(salaryEl);
  infoRow.appendChild(teamEl);
  content.appendChild(infoRow);

  // Stats section
  const statsSection = document.createElement('div');
  statsSection.style.cssText = 'display:flex;flex-direction:column;gap:3px;';

  const statsTitle = document.createElement('div');
  statsTitle.textContent = 'STATS';
  statsTitle.style.cssText = 'font-size:10px;color:var(--color-text-muted);letter-spacing:1px;margin-bottom:2px;';
  statsSection.appendChild(statsTitle);

  const statColors: Record<string, string> = {
    coding: 'var(--color-stat-coding)',
    art: 'var(--color-stat-art)',
    sound: 'var(--color-stat-sound)',
    design: 'var(--color-stat-design)',
  };

  const maxStat = Math.max(20, ...Object.values(staff.stats));
  for (const [stat, value] of Object.entries(staff.stats)) {
    statsSection.appendChild(statBar(stat, value, maxStat, statColors[stat] ?? 'var(--color-text-muted)'));
  }
  content.appendChild(statsSection);

  // XP bar
  const xpRow = statBar('XP', staff.xp, staff.xpToNextLevel, 'var(--color-xp)');
  content.appendChild(xpRow);

  // Morale / Energy / Satisfaction
  const conditionSection = document.createElement('div');
  conditionSection.style.cssText = 'display:flex;flex-direction:column;gap:3px;';

  const condTitle = document.createElement('div');
  condTitle.textContent = 'CONDITION';
  condTitle.style.cssText = 'font-size:10px;color:var(--color-text-muted);letter-spacing:1px;margin-bottom:2px;';
  conditionSection.appendChild(condTitle);

  const moraleColor = staff.morale > 60 ? 'var(--color-morale-high)' : staff.morale > 30 ? 'var(--color-morale-mid)' : 'var(--color-morale-low)';
  conditionSection.appendChild(statBar('Morale', staff.morale, 100, moraleColor));
  conditionSection.appendChild(statBar('Energy', staff.energy, 100, 'var(--color-info)'));
  conditionSection.appendChild(statBar('Satisfy', staff.satisfaction, 100, 'var(--color-success)'));
  content.appendChild(conditionSection);

  // Traits
  if (staff.traits.length > 0) {
    const traitsSection = document.createElement('div');
    traitsSection.style.cssText = 'display:flex;flex-direction:column;gap:2px;';

    const traitsTitle = document.createElement('div');
    traitsTitle.textContent = 'TRAITS';
    traitsTitle.style.cssText = 'font-size:10px;color:var(--color-text-muted);letter-spacing:1px;margin-bottom:2px;';
    traitsSection.appendChild(traitsTitle);

    for (const tid of staff.traits) {
      const def = theme.entities.traits.find((t) => t.id === tid);
      if (!def) continue;
      const traitEl = document.createElement('div');
      traitEl.style.cssText = 'font-size:11px;display:flex;gap:6px;';
      const nameEl = document.createElement('span');
      nameEl.textContent = def.name;
      nameEl.style.cssText = `font-weight:600;color:${def.rarity === 'rare' ? 'var(--color-warning)' : def.rarity === 'uncommon' ? 'var(--color-info)' : 'var(--color-text-primary)'};`;
      const descEl = document.createElement('span');
      descEl.textContent = def.description;
      descEl.style.cssText = 'color:var(--color-text-muted);';
      traitEl.appendChild(nameEl);
      traitEl.appendChild(descEl);
      traitsSection.appendChild(traitEl);
    }
    content.appendChild(traitsSection);
  }

  // Senior role
  if (staff.seniorRole) {
    const srDef = theme.entities.seniorRoles?.find((r) => r.id === staff.seniorRole);
    if (srDef) {
      const srEl = document.createElement('div');
      srEl.style.cssText = 'font-size:11px;color:var(--color-warning);font-weight:600;';
      srEl.textContent = `${srDef.name} — ${srDef.description}`;
      content.appendChild(srEl);
    }
  }

  // Status tags
  const statusTags: string[] = [];
  if (staff.isTraining) statusTags.push('Training');
  if (staff.isOnLeave) statusTags.push('On Leave');
  if (staff.isOnNotice) statusTags.push('Leaving Soon!');
  if (staff.demand) statusTags.push(`Wants: ${staff.demand.type.replace(/_/g, ' ')}`);

  if (statusTags.length > 0) {
    const statusEl = document.createElement('div');
    statusEl.style.cssText = 'font-size:11px;color:var(--color-warning);';
    statusEl.textContent = statusTags.join(' · ');
    content.appendChild(statusEl);
  }

  // Contribution
  if (staff.lifetimeContribution > 0) {
    const contribEl = document.createElement('div');
    contribEl.style.cssText = 'font-size:10px;color:var(--color-text-muted);';
    contribEl.textContent = `Lifetime contribution: ${Math.round(staff.lifetimeContribution)} points`;
    content.appendChild(contribEl);
  }

  // Action buttons
  const actionsRow = document.createElement('div');
  actionsRow.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap;margin-top:4px;';

  const makeActionBtn = (label: string, style: string, onClick: () => void) => {
    const btn = document.createElement('button');
    btn.className = `btn${style ? ` btn--${style}` : ''}`;
    btn.textContent = label;
    btn.style.cssText = 'padding:6px 12px;font-size:12px;';
    btn.addEventListener('click', onClick);
    return btn;
  };

  // Promote to senior role (not CEO, not already promoted)
  if (!staff.isCEO && !staff.seniorRole && theme.entities.seniorRoles) {
    const eligible = theme.entities.seniorRoles.filter((r) => staff.level >= r.minLevel);
    if (eligible.length > 0) {
      for (const role of eligible) {
        actionsRow.appendChild(makeActionBtn(`Promote: ${role.name}`, 'success', () => {
          const result = store.dispatch({ type: 'PROMOTE_STAFF', staffId: staff.id, seniorRoleId: role.id });
          if (result.success) {
            dialog.close();
          } else {
            errorEl.textContent = result.error ?? 'Failed';
          }
        }));
      }
    }
  }

  // Give Raise (not CEO)
  if (!staff.isCEO) {
    actionsRow.appendChild(makeActionBtn('Give Raise', '', () => {
      const newSalary = Math.round(staff.salary * 1.15);
      const result = store.dispatch({ type: 'GIVE_RAISE', staffId: staff.id, newSalary });
      if (result.success) {
        dialog.close();
      } else {
        errorEl.textContent = result.error ?? 'Failed';
      }
    }));
  }

  // Grant Leave
  if (!staff.isOnLeave && !staff.isTraining) {
    actionsRow.appendChild(makeActionBtn('Grant Leave', '', () => {
      const result = store.dispatch({ type: 'GRANT_LEAVE', staffId: staff.id });
      if (result.success) {
        dialog.close();
      } else {
        errorEl.textContent = result.error ?? 'Failed';
      }
    }));
  }

  // Fire (not CEO)
  if (!staff.isCEO) {
    actionsRow.appendChild(makeActionBtn('Fire', 'danger', () => {
      const result = store.dispatch({ type: 'FIRE_STAFF', staffId: staff.id });
      if (result.success) {
        dialog.close();
      } else {
        errorEl.textContent = result.error ?? 'Failed';
      }
    }));
  }

  content.appendChild(actionsRow);
  content.appendChild(errorEl);

  dialog.body.appendChild(content);
  dialog.mount(parent);
}
